/**
 * AUTO-GIFT — Ajoute automatiquement un produit cadeau au panier
 * quand un produit de la collection XYZ est présent dans le panier.
 *
 * NOUVEAU : La liste des produits déclencheurs est récupérée directement en JS
 * via l'endpoint public /collections/{handle}/products.json, avec pagination
 * automatique. Ça fonctionne peu importe la taille de la collection (2 produits
 * comme 600+), contrairement à une boucle Liquid qui est plafonnée à 50 produits
 * par défaut.
 *
 * Comportement :
 * - Au chargement de la page, le script récupère TOUS les IDs de produits de la
 *   collection déclencheur (avec pagination automatique)
 * - Si un produit déclencheur est ajouté et qu'aucun cadeau n'est dans le panier,
 *   on ajoute le premier cadeau DISPONIBLE de la liste giftOptions (dans l'ordre)
 * - Le cadeau N'EST JAMAIS retiré automatiquement par ce script, même si le produit
 *   déclencheur est retiré du panier. C'est la remise automatique Shopify "Achetez X,
 *   obtenez Y" qui cessera de s'appliquer d'elle-même (le cadeau redevient payant)
 * - Si le client retire le cadeau MANUELLEMENT, le script respecte ce choix et ne le
 *   rajoute pas automatiquement par la suite
 *
 * INSTALLATION :
 * 1. Colle ce fichier dans /assets/auto-gift.js de ton thème
 * 2. Ajoute ceci juste avant </body> dans theme.liquid (BEAUCOUP plus simple
 *    qu'avant, plus besoin de boucle Liquid) :
 *
 *    <script>
 *      window.autoGiftConfig = {
 *        collectionHandle: "xyz-collection-handle",
 *        giftOptions: [
 *          { handle: "produit-cadeau-a", variantId: 1111111111 },
 *          { handle: "produit-cadeau-b", variantId: 2222222222 }
 *        ]
 *      };
 *    </script>
 *    <script src="{{ 'auto-gift.js' | asset_url }}" defer></script>
 *
 * 3. Remplace "xyz-collection-handle" par le handle réel de ta collection
 * 4. Pour chaque cadeau : "handle" = le handle du produit, "variantId" = l'ID de
 *    la variante précise (couleur) que tu veux offrir
 * 5. Une remise automatique "Achetez X, obtenez Y" à 100% doit être active dans
 *    Shopify pour CHAQUE produit cadeau de la liste
 */

(function () {
  const config = window.autoGiftConfig || {};
  const COLLECTION_HANDLE = config.collectionHandle;
  const GIFT_OPTIONS = config.giftOptions || [];
  const GIFT_PROPERTY_KEY = "_auto_gift";

  const KEY_DISMISSED = "autoGift_dismissed";
  const KEY_WAS_PRESENT = "autoGift_wasPresent";

  if (!COLLECTION_HANDLE || GIFT_OPTIONS.length === 0) {
    console.warn("Auto-gift: configuration manquante (collectionHandle ou giftOptions).");
    return;
  }

  let isProcessing = false;
  const productCache = {};
  let triggerIdsPromise = null;

  function getFlag(key) {
    return sessionStorage.getItem(key) === "true";
  }
  function setFlag(key, value) {
    sessionStorage.setItem(key, value ? "true" : "false");
  }

  // Récupère TOUS les IDs produits de la collection, avec pagination automatique
  // (endpoint public /collections/{handle}/products.json, max 250 par page)
  async function loadTriggerProductIds() {
    const ids = [];
    let page = 1;
    const limit = 250;

    while (true) {
      let data;
      try {
        const res = await fetch(
          `/collections/${COLLECTION_HANDLE}/products.json?limit=${limit}&page=${page}`
        );
        if (!res.ok) break;
        data = await res.json();
      } catch (e) {
        console.error("Auto-gift: erreur lors du chargement de la collection", e);
        break;
      }

      const products = data.products || [];
      if (products.length === 0) break;

      ids.push(...products.map((p) => p.id));

      if (products.length < limit) break; // dernière page atteinte
      page++;
    }

    console.log(`Auto-gift: ${ids.length} produits déclencheurs chargés depuis "${COLLECTION_HANDLE}"`);
    return ids;
  }

  async function getCart() {
    const res = await fetch("/cart.js");
    return res.json();
  }

  function findGiftLine(cart) {
    return cart.items.find(
      (item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === "true"
    );
  }

  async function getProductData(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      console.error(`Auto-gift: impossible de charger le produit "${handle}"`, e);
      return null;
    }
  }

  async function isVariantAvailable(handle, variantId) {
    const product = await getProductData(handle);
    if (!product) return false;
    const variant = product.variants.find((v) => v.id === variantId);
    return !!(variant && variant.available);
  }

  async function findFirstAvailableGift() {
    for (const option of GIFT_OPTIONS) {
      const available = await isVariantAvailable(option.handle, option.variantId);
      if (available) return option;
    }
    return null;
  }

  async function addGift(variantId) {
    await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [GIFT_PROPERTY_KEY]: "true" },
          },
        ],
      }),
    });
  }

  async function syncGift() {
    if (isProcessing) return;
    isProcessing = true;
    try {
      const triggerIds = await triggerIdsPromise;
      const cart = await getCart();
      const hasTrigger = cart.items.some((item) => triggerIds.includes(item.product_id));
      const giftLine = findGiftLine(cart);
      const wasPresent = getFlag(KEY_WAS_PRESENT);
      const dismissed = getFlag(KEY_DISMISSED);

      if (giftLine) {
        setFlag(KEY_WAS_PRESENT, true);
        return;
      }

      if (!hasTrigger) {
        return;
      }

      if (wasPresent) {
        setFlag(KEY_DISMISSED, true);
        setFlag(KEY_WAS_PRESENT, false);
        return;
      }

      if (dismissed) {
        return;
      }

      const chosenGift = await findFirstAvailableGift();
      if (!chosenGift) {
        console.warn("Auto-gift: tous les produits cadeaux sont épuisés.");
        return;
      }

      await addGift(chosenGift.variantId);
      setFlag(KEY_WAS_PRESENT, true);
    } catch (e) {
      console.error("Auto-gift: erreur de synchronisation", e);
    } finally {
      isProcessing = false;
    }
  }

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = args[0];
    const url = typeof request === "string" ? request : request?.url || "";
    const fetchPromise = originalFetch.apply(this, args);

    if (/\/cart\/(add|change|update)\.js(\?|$)/.test(url)) {
      fetchPromise
        .then((response) => {
          response
            .clone()
            .json()
            .then(() => syncGift())
            .catch(() => {});
        })
        .catch(() => {});
    }

    return fetchPromise;
  };

  // Charge la liste des produits déclencheurs une seule fois au chargement de la page
  triggerIdsPromise = loadTriggerProductIds();

  document.addEventListener("DOMContentLoaded", syncGift);
})();